// pages/Dashboard/Billing.js
import { useState, useMemo, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  FaPlus,
  FaTrash,
  FaMoneyBillWave,
  FaMobileAlt,
  FaQrcode,
  FaCreditCard,
  FaTags,
  FaPercentage,
  FaPrint,
  FaSave,
  FaUserCircle,
  FaSearch,
  FaReceipt,
} from 'react-icons/fa';
import '../Css/Billing.css';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../components/Toast';
import { baseurl } from '../Baseurl';

const PAYMENT_METHODS = [
  { key: 'cash', label: 'Cash', icon: <FaMoneyBillWave /> },
  { key: 'upi', label: 'UPI', icon: <FaMobileAlt /> },
  { key: 'qr', label: 'QR Code', icon: <FaQrcode /> },
  { key: 'card', label: 'Card', icon: <FaCreditCard /> },
];

const TAX_OPTIONS = [0, 3, 5, 8, 10, 12, 15, 18, 25];

let idCounter = 1;

function Billing() {
  const { uuid } = useParams();
  const [searchParams] = useSearchParams();
  const rulePage = searchParams.get('rule_page');
  const apId = searchParams.get('ap_id');
  const action = searchParams.get('action');

  const isEditMode = Boolean(uuid) && action === 'edit';
  const isViewMode = Boolean(uuid) && !isEditMode;
  const isFromAppointment = rulePage === 'appointment' && Boolean(apId);

  const toast = useToast();

  const [servicesCatalog, setServicesCatalog] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [loadingInvoice, setLoadingInvoice] = useState(Boolean(uuid));
  const [appointmentId, setAppointmentId] = useState(null);
  const [lockedItemId, setLockedItemId] = useState(null);

  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [discountType, setDiscountType] = useState('percent');
  const [discountValue, setDiscountValue] = useState(0);
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(null);
  const [taxPercent, setTaxPercent] = useState(0);
  const [splitPayments, setSplitPayments] = useState([{ method: 'cash', amount: '' }]);
  const [saving, setSaving] = useState(false);


  const navigate = useNavigate();


  const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
  });

  /* -------------------- FETCH SERVICES & STAFF CATALOG -------------------- */
  useEffect(() => {
    const fetchCatalog = async () => {
      setLoadingCatalog(true);
      try {
        const [svcRes, staffRes] = await Promise.all([
          fetch(`${baseurl}/services?status=active`, { headers: authHeaders() }),
          fetch(`${baseurl}/staff?status=active`, { headers: authHeaders() }),
        ]);
        const svcData = await svcRes.json();
        const staffData = await staffRes.json();

        if (!svcRes.ok) throw new Error(svcData.message || 'Failed to fetch services');
        if (!staffRes.ok) throw new Error(staffData.message || 'Failed to fetch staff');

        setServicesCatalog(svcData.data.services);
        setStaffList(staffData.data.staff);
      } catch (err) {
        toast(err.message || 'Failed to load catalog', 'error');
      } finally {
        setLoadingCatalog(false);
      }
    };

    fetchCatalog();
  }, []);

  /* -------------------- LOAD INVOICE (edit/view mode) -------------------- */
  useEffect(() => {
    if (!uuid) return;

    const fetchInvoice = async () => {
      setLoadingInvoice(true);
      try {
        const res = await fetch(`${baseurl}/invoices/${uuid}`, { headers: authHeaders() });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || 'Failed to fetch invoice');

        const inv = data.data.invoice;
        setInvoiceNumber(inv.invoice_number);
        setClientName(inv.client_name);
        setClientPhone(inv.client_phone || '');
        setDiscountType(inv.discount_type);
        setDiscountValue(Number(inv.discount_value));
        setTaxPercent(Number(inv.tax_percent));
        if (inv.coupon_code) {
          setCouponCode(inv.coupon_code);
          setCouponApplied({ code: inv.coupon_code, percent: Number(inv.coupon_percent) });
        }
        setItems(
          inv.items.map((it) => ({
            id: idCounter++,
            serviceId: it.service_id,
            serviceName: it.service_name,
            staffId: it.staff_id,
            staffName: it.staff_name,
            price: Number(it.price),
            qty: it.qty,
          }))
        );
        setSplitPayments(
          inv.payments.length > 0
            ? inv.payments.map((p) => ({ method: p.method, amount: String(p.amount) }))
            : [{ method: 'cash', amount: '' }]
        );
        if (inv.appointment_id) setAppointmentId(inv.appointment_id);
      } catch (err) {
        toast(err.message || 'Failed to load invoice', 'error');
      } finally {
        setLoadingInvoice(false);
      }
    };

    fetchInvoice();
  }, [uuid]);

  /* -------------------- LOAD FROM APPOINTMENT -------------------- */
  useEffect(() => {
    if (!isFromAppointment || uuid) return;

    const fetchAppointmentDetails = async () => {
      setLoadingInvoice(true);
      try {
        const res = await fetch(`${baseurl}/invoices/appointment-details?ap_id=${encodeURIComponent(apId)}`, {
          headers: authHeaders(),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || 'Failed to fetch appointment');

        const appt = data.data.appointment;

        if (appt.existingInvoiceUuid) {
          toast('An invoice already exists for this appointment', 'error');
        }

        setClientName(appt.client_name);
        setClientPhone(appt.client_phone || '');
        setAppointmentId(appt.id);

        const newItemId = idCounter++;
        setLockedItemId(newItemId);
        setItems([
          {
            id: newItemId,
            serviceId: appt.service_id,
            serviceName: appt.service_name,
            staffId: appt.staff_id,
            staffName: appt.staff_name,
            price: Number(appt.service_price),
            qty: 1,
          },
        ]);
      } catch (err) {
        toast(err.message || 'Failed to load appointment details', 'error');
      } finally {
        setLoadingInvoice(false);
      }
    };

    fetchAppointmentDetails();
  }, [isFromAppointment, apId, uuid]);

  /* -------------------- DEFAULT BLANK ITEM (plain new invoice) -------------------- */
  useEffect(() => {
    if (!uuid && !isFromAppointment && items.length === 0 && servicesCatalog.length > 0 && staffList.length > 0) {
      setItems([
        {
          id: idCounter++,
          serviceId: servicesCatalog[0].id,
          serviceName: servicesCatalog[0].name,
          staffId: staffList[0].id,
          staffName: staffList[0].name,
          price: Number(servicesCatalog[0].price),
          qty: 1,
        },
      ]);
    }
  }, [servicesCatalog, staffList, uuid, isFromAppointment, items.length]);

  const filteredCatalog = useMemo(
    () => servicesCatalog.filter((s) => s.name.toLowerCase().includes(search.toLowerCase())),
    [search, servicesCatalog]
  );

  const addItem = (service) => {
    setItems((prev) => [
      ...prev,
      {
        id: idCounter++,
        serviceId: service.id,
        serviceName: service.name,
        staffId: staffList[0]?.id || '',
        staffName: staffList[0]?.name || '',
        price: Number(service.price),
        qty: 1,
      },
    ]);
    setSearch('');
  };

  const removeItem = (id) => {
    if (id === lockedItemId) {
      toast('This service is linked to the appointment and cannot be removed', 'error');
      return;
    }
    setItems((prev) => prev.filter((i) => i.id !== id));
  };

  const updateItem = (id, field, value) => {
    if (id === lockedItemId && (field === 'serviceId' || field === 'staffId')) {
      toast('This item is locked because it came from an appointment', 'error');
      return;
    }

    setItems((prev) =>
      prev.map((i) => {
        if (i.id !== id) return i;

        if (field === 'serviceId') {
          const svc = servicesCatalog.find((s) => s.id === Number(value));
          return { ...i, serviceId: Number(value), serviceName: svc?.name, price: Number(svc?.price || 0) };
        }
        if (field === 'staffId') {
          const st = staffList.find((s) => s.id === Number(value));
          return { ...i, staffId: Number(value), staffName: st?.name };
        }
        return { ...i, [field]: value };
      })
    );
  };

  const subtotal = items.reduce((sum, item) => sum + item.price * item.qty, 0);

  const applyCoupon = () => {
    const code = couponCode.trim().toUpperCase();
    if (code === 'SALON10') {
      setCouponApplied({ code, percent: 10 });
    } else if (code === 'FIRSTVISIT') {
      setCouponApplied({ code, percent: 15 });
    } else {
      setCouponApplied(null);
      toast('Invalid coupon code', 'error');
    }
  };

  const manualDiscount =
    discountType === 'percent' ? (subtotal * Number(discountValue || 0)) / 100 : Number(discountValue || 0);

  const couponDiscount = couponApplied ? (subtotal * couponApplied.percent) / 100 : 0;

  const totalDiscount = manualDiscount + couponDiscount;
  const afterDiscount = Math.max(subtotal - totalDiscount, 0);
  const taxAmount = (afterDiscount * Number(taxPercent)) / 100;
  const grandTotal = afterDiscount + taxAmount;

  const totalPaid = splitPayments.reduce((sum, p) => sum + Number(p.amount || 0), 0);
  const dueAmount = Math.max(grandTotal - totalPaid, 0);
  const changeDue = Math.max(totalPaid - grandTotal, 0);

  const addSplitRow = () => {
    setSplitPayments((prev) => [...prev, { method: 'cash', amount: '' }]);
  };

  const removeSplitRow = (index) => {
    setSplitPayments((prev) => prev.filter((_, i) => i !== index));
  };

  const updateSplitRow = (index, field, value) => {
    setSplitPayments((prev) => prev.map((p, i) => (i === index ? { ...p, [field]: value } : p)));
  };

  const fillRemaining = (index) => {
    const otherTotal = splitPayments.reduce((sum, p, i) => (i === index ? sum : sum + Number(p.amount || 0)), 0);
    const remaining = Math.max(grandTotal - otherTotal, 0).toFixed(2);
    updateSplitRow(index, 'amount', remaining);
  };

  const handleGenerate = async () => {
  if (!clientName.trim()) {
    toast('Please enter client name', 'error');
    return;
  }
  if (items.length === 0) {
    toast('Add at least one service', 'error');
    return;
  }

  const status = dueAmount <= 0 ? 'paid' : totalPaid > 0 ? 'partial' : 'due';

  const payload = {
    appointmentId: appointmentId || null,
    clientName,
    clientPhone,
    items: items.map((i) => ({
      serviceId: i.serviceId,
      serviceName: i.serviceName,
      staffId: i.staffId,
      staffName: i.staffName,
      price: i.price,
      qty: i.qty,
    })),
    discountType,
    discountValue: Number(discountValue || 0),
    couponCode: couponApplied?.code || null,
    couponPercent: couponApplied?.percent || 0,
    taxPercent: Number(taxPercent),
    subtotal,
    taxAmount,
    grandTotal,
    payments: splitPayments,
    totalPaid,
    dueAmount,
    status,
  };

  setSaving(true);
  try {
    const url = isEditMode ? `${baseurl}/invoices/${uuid}` : `${baseurl}/invoices`;
    const res = await fetch(url, {
      method: isEditMode ? 'PUT' : 'POST',
      headers: authHeaders(),
      body: JSON.stringify(payload),
    });

    const data = await res.json();
    if (!res.ok) {
      if (data?.data?.existingInvoiceUuid) {
        toast(data.message || 'Invoice already exists for this appointment', 'error');
        navigate(`/dashboard/invoice/${data.data.existingInvoiceUuid}`);
        return;
      }
      throw new Error(data.message || 'Failed to save invoice');
    }

    toast(data.message || 'Invoice generated successfully', 'success');
    navigate(`/dashboard/invoice/${data.data.invoice.uuid}`);
  } catch (err) {
    toast(err.message || 'Failed to save invoice', 'error');
  } finally {
    setSaving(false);
  }
};

  const isReadOnly = isViewMode;

  if (loadingCatalog || loadingInvoice) {
    return <div className="billing-loading">Loading invoice...</div>;
  }

  return (
    <div className="billing-page">
      <div className="billing-header">
        <div>
          <h2>{isEditMode ? 'Edit Invoice' : isViewMode ? 'View Invoice' : 'New Invoice'}</h2>
          <p>{invoiceNumber ? `Invoice ${invoiceNumber}` : 'Create a fast, accurate bill for your client'}</p>
        </div>
        <div className="header-actions">
          <button className="btn-outline">
            <FaPrint /> Print
          </button>
          {!isReadOnly && (
            <button className="btn-primary" onClick={handleGenerate} disabled={saving}>
              <FaReceipt /> {saving ? 'Saving...' : isEditMode ? 'Update Invoice' : 'Generate Invoice'}
            </button>
          )}
        </div>
      </div>

      <div className="billing-grid">
        {/* LEFT: Builder */}
        <div className="billing-main">
          {/* Client Info */}
          <div className="panel">
            <h3 className="panel-title">Client Details</h3>
            <div className="client-row">
              <div className="input-group">
                <FaUserCircle className="input-icon" />
                <input
                  type="text"
                  placeholder="Client name"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  disabled={isReadOnly}
                />
              </div>
              <div className="input-group">
                <FaMobileAlt className="input-icon" />
                <input
                  type="tel"
                  placeholder="Phone number"
                  value={clientPhone}
                  onChange={(e) => setClientPhone(e.target.value)}
                  disabled={isReadOnly}
                />
              </div>
            </div>
          </div>

          {/* Add Services */}
          <div className="panel">
            <h3 className="panel-title">Add Services / Products</h3>
            {!isReadOnly && (
              <div className="input-group search-box">
                <FaSearch className="input-icon" />
                <input
                  type="text"
                  placeholder="Search services (e.g. Haircut, Facial)"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            )}
            {search && !isReadOnly && (
              <div className="search-results">
                {filteredCatalog.map((svc) => (
                  <div key={svc.id} className="search-result-item" onClick={() => addItem(svc)}>
                    <span>{svc.name}</span>
                    <span className="svc-price">₹{svc.price}</span>
                  </div>
                ))}
                {filteredCatalog.length === 0 && <div className="search-result-empty">No services found</div>}
              </div>
            )}

            <div className="items-table">
              <div className="items-table-head">
                <span>Service</span>
                <span>Staff</span>
                <span>Qty</span>
                <span>Price</span>
                <span>Total</span>
                <span></span>
              </div>
              {items.map((item) => {
                const locked = item.id === lockedItemId;
                return (
                  <div className="items-table-row" key={item.id}>
                    <select
                      value={item.serviceId}
                      onChange={(e) => updateItem(item.id, 'serviceId', e.target.value)}
                      disabled={isReadOnly || locked}
                    >
                      {servicesCatalog.map((s) => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                    <select
                      value={item.staffId}
                      onChange={(e) => updateItem(item.id, 'staffId', e.target.value)}
                      disabled={isReadOnly || locked}
                    >
                      {staffList.map((st) => (
                        <option key={st.id} value={st.id}>{st.name}</option>
                      ))}
                    </select>
                    <input
                      type="number"
                      min="1"
                      value={item.qty}
                      onChange={(e) => updateItem(item.id, 'qty', Number(e.target.value))}
                      disabled={isReadOnly}
                    />
                    <span className="cell-price">₹{item.price}</span>
                    <span className="cell-total">₹{item.price * item.qty}</span>
                    {!isReadOnly && (
                      <button className="row-delete-btn" onClick={() => removeItem(item.id)}>
                        <FaTrash />
                      </button>
                    )}
                  </div>
                );
              })}
              {items.length === 0 && <div className="items-empty">No services added yet</div>}
            </div>
          </div>

          {/* Discounts & Coupons */}
          <div className="panel">
            <h3 className="panel-title">Discounts & Offers</h3>
            <div className="discount-row">
              <div className="discount-type-toggle">
                <button
                  className={discountType === 'percent' ? 'active' : ''}
                  onClick={() => setDiscountType('percent')}
                  disabled={isReadOnly}
                >
                  <FaPercentage /> %
                </button>
                <button
                  className={discountType === 'flat' ? 'active' : ''}
                  onClick={() => setDiscountType('flat')}
                  disabled={isReadOnly}
                >
                  ₹ Flat
                </button>
              </div>
              <input
                type="number"
                min="0"
                placeholder="Discount value"
                value={discountValue}
                onChange={(e) => setDiscountValue(e.target.value)}
                className="discount-input"
                disabled={isReadOnly}
              />
            </div>

            {/* <div className="coupon-row">
              <div className="input-group">
                <FaTags className="input-icon" />
                <input
                  type="text"
                  placeholder="Coupon code (e.g. SALON10)"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                  disabled={isReadOnly}
                />
              </div>
              {!isReadOnly && (
                <button className="btn-outline" onClick={applyCoupon}>
                  Apply
                </button>
              )}
            </div>
            {couponApplied && (
              <div className="coupon-applied">
                Coupon <strong>{couponApplied.code}</strong> applied — {couponApplied.percent}% off
              </div>
            )} */}

            <div className="tax-row">
              <label className="field-label">Tax (%)</label>
              <select
                value={taxPercent}
                onChange={(e) => setTaxPercent(Number(e.target.value))}
                disabled={isReadOnly}
                className="tax-select"
              >
                {TAX_OPTIONS.map((t) => (
                  <option key={t} value={t}>{t}%</option>
                ))}
              </select>
            </div>
          </div>

          {/* Payment */}
          <div className="panel">
            <h3 className="panel-title">Payment</h3>
            <p className="panel-subtitle">Accept full or partial payment, split across methods</p>

            {splitPayments.map((p, index) => (
              <div className="split-row" key={index}>
                <div className="method-select">
                  {PAYMENT_METHODS.map((m) => (
                    <button
                      key={m.key}
                      className={`method-chip ${p.method === m.key ? 'active' : ''}`}
                      onClick={() => updateSplitRow(index, 'method', m.key)}
                      type="button"
                      disabled={isReadOnly}
                    >
                      {m.icon} {m.label}
                    </button>
                  ))}
                </div>
                <input
                  type="number"
                  min="0"
                  placeholder="Amount"
                  value={p.amount}
                  onChange={(e) => updateSplitRow(index, 'amount', e.target.value)}
                  className="split-amount-input"
                  disabled={isReadOnly}
                />
                {!isReadOnly && (
                  <button className="link-btn" onClick={() => fillRemaining(index)} type="button">
                    Fill remaining
                  </button>
                )}
                {!isReadOnly && splitPayments.length > 1 && (
                  <button className="row-delete-btn" onClick={() => removeSplitRow(index)} type="button">
                    <FaTrash />
                  </button>
                )}
              </div>
            ))}

            {!isReadOnly && (
              <button className="add-split-btn" onClick={addSplitRow} type="button">
                <FaPlus /> Add another payment method
              </button>
            )}
          </div>
        </div>

        {/* RIGHT: Summary */}
        <div className="billing-summary">
          <div className="summary-panel">
            <h3 className="panel-title">Invoice Summary</h3>

            <div className="summary-row">
              <span>Subtotal</span>
              <span>₹{subtotal.toFixed(2)}</span>
            </div>
            {manualDiscount > 0 && (
              <div className="summary-row discount">
                <span>Discount</span>
                <span>-₹{manualDiscount.toFixed(2)}</span>
              </div>
            )}
            {couponDiscount > 0 && (
              <div className="summary-row discount">
                <span>Coupon ({couponApplied.code})</span>
                <span>-₹{couponDiscount.toFixed(2)}</span>
              </div>
            )}
            <div className="summary-row">
              <span>Tax ({taxPercent}%)</span>
              <span>₹{taxAmount.toFixed(2)}</span>
            </div>
            <div className="summary-divider"></div>
            <div className="summary-row grand-total">
              <span>Grand Total</span>
              <span>₹{grandTotal.toFixed(2)}</span>
            </div>

            <div className="summary-divider"></div>

            <div className="summary-row">
              <span>Amount Paid</span>
              <span>₹{totalPaid.toFixed(2)}</span>
            </div>
            {dueAmount > 0 && (
              <div className="summary-row due">
                <span>Balance Due</span>
                <span>₹{dueAmount.toFixed(2)}</span>
              </div>
            )}
            {changeDue > 0 && (
              <div className="summary-row change">
                <span>Change to Return</span>
                <span>₹{changeDue.toFixed(2)}</span>
              </div>
            )}

            {!isReadOnly && (
              <button className="btn-primary full-width" onClick={handleGenerate} disabled={saving}>
                <FaSave /> {saving ? 'Saving...' : isEditMode ? 'Update Invoice' : 'Save & Generate Invoice'}
              </button>
            )}

            {dueAmount > 0 && (
              <p className="due-note">
                This will be saved as a <strong>partial payment</strong>. Balance can be collected later.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Billing;